/**
 * How one ending of a best-of-3 Table Tennis match is written and ordered on screen — the whole of it,
 * in one place, because the market card and the modeling board both state the same four endings and a
 * pick named differently in the two would read as a different bet.
 *
 * <p>The backend states an ending from the **first** competitor's point of view (`2-0`, `2-1`, `1-2`,
 * `0-2`), which is also what the selection reference holds, what settlement keys on and what goes out
 * to GR8 as the outcome value. Here it is restated from the point of view of the player who **wins**
 * it, so `0-2` reads as `<second player> 2-0`. That is a display and nothing more: the raw reference
 * stays on the selection's own name, one hover away, and nothing else in the UI translates it.
 */

/** An ending as the backend states it: sets the first competitor takes, then the second's. */
export interface TtMatchScore {
  setsA: number;
  setsB: number;
}

/** Sets first, second, both whole numbers and nothing else around them. */
const REFERENCE = /^(\d+)-(\d+)$/;

/**
 * The ending a selection reference names, or null when it names none. A reference this cannot read must
 * leave the caller showing the picks it already has rather than dropping them: a shape we have not seen
 * is worth looking plain, not worth hiding a market that is taking bets.
 *
 * <p>The set counts themselves are not checked against a format — the naming rule below holds for any
 * of them, so a longer match's `3-0` reads correctly without this module knowing the format. A **level**
 * score is refused, and that is the one arithmetic rule here: it names no winner, so there is no player
 * to state it from the side of, and `1-1` would otherwise be shown as one of them winning it.
 */
export function parseMatchScore(reference?: string | null): TtMatchScore | null {
  const match = REFERENCE.exec((reference ?? '').trim());
  if (!match) {
    return null;
  }
  const score = { setsA: Number(match[1]), setsB: Number(match[2]) };
  return score.setsA === score.setsB ? null : score;
}

/** Whether the first competitor is the one who wins this ending. */
export const wonByFirst = (score: TtMatchScore): boolean => score.setsA > score.setsB;

/**
 * The ending as a pick reads: the player who takes it, then the score from their side. The separator is
 * the same plain hyphen the reference uses, so the two can be told apart by the name in front of them
 * and by nothing else.
 */
export function matchScoreLabel(score: TtMatchScore, nameA: string, nameB: string): string {
  const winner = wonByFirst(score) ? nameA : nameB;
  const won = Math.max(score.setsA, score.setsB);
  const lost = Math.min(score.setsA, score.setsB);
  return `${winner} ${won}-${lost}`;
}

/**
 * Display order: the first competitor's endings, then the second's, and within a player the straight-sets
 * win before the three-setter. It is deliberately not the order the backend states them in — grouping
 * the two ways one player can win puts the four picks together as one book, which is how a four-way
 * market is read.
 */
export function compareMatchScores(left: TtMatchScore, right: TtMatchScore): number {
  if (wonByFirst(left) !== wonByFirst(right)) {
    return wonByFirst(left) ? -1 : 1;
  }
  const setsLost = (score: TtMatchScore) => Math.min(score.setsA, score.setsB);
  return setsLost(left) - setsLost(right);
}
